import i18n from '../../../../base-dictionary';
import { newCOLORS } from '../../../../styles/colors';
import type { LegendItem } from '../initiative-performance.type';
import { InitiativeMetricType } from '../initiative-performance.type';

/**
 * Returns an array of legend items based on the given metric type.
 *
 * @param {InitiativeMetricType} metric - The type of metric to generate legend items for.
 * @return {LegendItem[]} An array of legend items with titles, descriptions, and colors.
 */
const getLegendItems = (metric: InitiativeMetricType): LegendItem[] => {
  const metricLabels = {
    [InitiativeMetricType.Tasks]: i18n.t('common.work_units'),
    [InitiativeMetricType.Points]: i18n.t('common.estimation'),
  };

  return [
    {
      title: i18n.t('initiatives.initiative_performance.section_status.total_tasks', {
        metric: metricLabels[metric],
      }),
      description: i18n.t('initiatives.initiative_performance.section_status.cumulative_for_initiative', {
        initiative: i18n.t('common.initiative'),
      }),
      color: newCOLORS.blue,
    },
    {
      title: i18n.t('initiatives.initiative_performance.section_status.completed_tasks', {
        metric: metricLabels[metric],
      }),
      description: i18n.t('initiatives.initiative_performance.section_status.cumulative_for_initiative', {
        initiative: i18n.t('common.initiative'),
      }),
      color: newCOLORS.green,
    },
    {
      title: 'Projection',
      description: 'future projection',
      color: newCOLORS.darkerGray2,
    },
  ];
};

/**
 * Formats the tooltip value based on the metric type.
 *
 * @param {string} value - The value to format.
 * @param {string} name - The name of the value.
 * @param {object} props - The props of the value.
 * @param {InitiativeMetricType} metric - The type of metric to format the value for.
 * @return {any} The formatted value.
 */
const formatTooltipValue = (
  value: string,
  name: string,
  props: { payload: { isFuturePoint: boolean }; dataKey: string },
  metric: InitiativeMetricType,
): [any, string | null] => {
  // If this is a future point, only show future values
  if (props.payload.isFuturePoint) {
    if (props.dataKey.includes('current')) return [null, null];
  }
  // If this is a current point, only show current values
  else {
    if (props.dataKey.includes('future')) return [null, null];
  }
  return [value, formatTooltipLabel(name, metric)];
};

/**
 * Formats the tooltip label based on the metric type.
 *
 * @param {string} value - The value to format.
 * @param {InitiativeMetricType} metric - The type of metric to format the label for.
 * @return {string} The formatted label.
 */
const formatTooltipLabel = (value: string, metric: InitiativeMetricType): string => {
  if (value.includes('total_tasks') && metric === InitiativeMetricType.Tasks)
    return `Total ${i18n.t('common.work_units')}`;

  if (value.includes('completed_tasks') && metric === InitiativeMetricType.Tasks)
    return `Completed ${i18n.t('common.work_units')}`;

  if (value.includes('total_tasks') && metric === InitiativeMetricType.Points)
    return `Total ${i18n.t('common.estimation')}`;

  if (value.includes('completed_tasks') && metric === InitiativeMetricType.Points)
    return `Completed ${i18n.t('common.estimation')}`;

  return value;
};

export { formatTooltipLabel, formatTooltipValue, getLegendItems };
